import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button Component - Design System Atmosférico v3.0
 * 
 * Geometria:
 * - btn-primary: Pill (9999px) + Gold gradient
 * - btn-coral: Pill + Coral + generating pulse
 * - UI Chrome: Micro (2px)
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-void-absolute disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-target",
  {
    variants: {
      variant: {
        // PRIMARY CTA - Pill Gold (Decreto v3.0)
        default:
          "rounded-pill bg-gradient-to-br from-gold-primary to-gold-soft text-void-absolute uppercase tracking-wider shadow-gold-glow duration-snap ease-spring hover:scale-[1.02] active:scale-[0.96]",

        // HERO (Legacy alias for default)
        hero:
          "rounded-pill bg-gradient-to-br from-gold-primary to-gold-soft text-void-absolute uppercase tracking-wider shadow-gold-glow duration-snap ease-spring hover:scale-[1.02] active:scale-[0.96]",

        // CORAL CTA - Para ações de geração
        coral:
          "rounded-pill bg-coral-electric text-void-absolute uppercase tracking-wider shadow-coral-glow duration-snap ease-spring hover:scale-[1.02] active:scale-[0.96]",

        // GENERATING - Com pulse animation
        generating:
          "rounded-pill bg-coral-electric text-void-absolute uppercase tracking-wider animate-generating duration-snap",

        // UI Chrome - Sharp Industrial
        secondary:
          "rounded-micro bg-void-soft text-textPrimary border border-void-warm duration-instant hover:bg-void-warm hover:border-gold-primary/20",

        // Outline Gold
        outline:
          "rounded-micro border border-gold-soft/30 bg-transparent text-gold-primary duration-snap hover:bg-gold-mist hover:border-gold-primary",

        // Ghost
        ghost:
          "rounded-micro hover:bg-void-warm hover:text-textPrimary duration-instant",

        // Destructive
        destructive:
          "rounded-micro bg-destructive text-destructive-foreground hover:bg-destructive/90",

        // Link
        link:
          "text-gold-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-14 px-8 py-2 text-sm",
        sm: "h-10 px-4 text-xs",
        lg: "h-16 px-12 text-base",
        icon: "h-14 w-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isGenerating?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isGenerating = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const activeVariant = isGenerating ? "generating" : variant;

    return (
      <Comp
        className={cn(buttonVariants({ variant: activeVariant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
